import { useEffect, useMemo, useRef, useState } from 'react';
import api from '../api';
import './home.css';

// Minimal shared helpers (kept local so existing Lurker stays untouched)
const _scriptPromises = new Map();
const loadScriptOnce = (src) => {
  if (!src) return Promise.reject(new Error('missing src'));
  if (_scriptPromises.has(src)) return _scriptPromises.get(src);
  const existing = document.querySelector(`script[src='${src}']`);
  const p = new Promise((resolve, reject) => {
    const done = () => resolve();
    const fail = (e) => reject(e || new Error('script load failed'));
    if (/player\.twitch\.tv\/js\/embed\/v1\.js/i.test(src) && window.Twitch) return done();
    if (existing) {
      const alreadyLoaded = existing.dataset && existing.dataset.loaded === '1';
      if (alreadyLoaded) return done();
      existing.addEventListener('load', () => {
        try { existing.dataset.loaded = '1'; } catch {}
        done();
      }, { once: true });
      existing.addEventListener('error', fail, { once: true });
      return;
    }
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = () => {
      try { s.dataset.loaded = '1'; } catch {}
      done();
    };
    s.onerror = fail;
    document.head.appendChild(s);
  });
  _scriptPromises.set(src, p);
  return p;
};

let _twitchInitChain = Promise.resolve();
const enqueueTwitchInit = (fn) => {
  const run = () => Promise.resolve().then(fn);
  const next = _twitchInitChain.then(run, run);
  _twitchInitChain = next.catch(() => {});
  return next;
};

function getEmbedParents() {
  const host = (typeof window !== 'undefined' ? window.location.hostname : '') || 'localhost';
  const alt = host.startsWith('www.') ? host.slice(4) : `www.${host}`;
  return Array.from(new Set([host, alt].filter(Boolean)));
}

function createSessionId() {
  try {
    const key = 'lurker2_session_id';
    const existing = sessionStorage.getItem(key);
    if (existing) return existing;
    const id = (crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`).slice(0, 64);
    sessionStorage.setItem(key, id);
    return id;
  } catch {
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`.slice(0, 64);
  }
}

async function logEvent(payload) {
  try {
    const sessionId = createSessionId();
    const body = {
      sessionId,
      hostname: window.location.hostname,
      path: window.location.pathname,
      userAgent: navigator.userAgent,
      ...payload,
    };
    await api.post('/lurker-logs/ingest', body).catch(() => {});
  } catch {
    /* ignore */
  }
}

function TwitchPlayer({ channel }) {
  const mountRef = useRef(null);
  const playerRef = useRef(null);
  const mountId = useMemo(() => `lurker2-twitch-${Math.random().toString(36).slice(2)}`, []);

  useEffect(() => {
    let mounted = true;
    if (!channel) return;

    const init = async () => {
      try {
        await enqueueTwitchInit(async () => {
          await logEvent({ platform: 'twitch', event: 'LURKER2_TWITCH_SCRIPT_LOAD_START', severity: 'info', message: `channel=${channel}` });
          await loadScriptOnce('https://player.twitch.tv/js/embed/v1.js');
          await logEvent({ platform: 'twitch', event: 'LURKER2_TWITCH_SCRIPT_LOAD_OK', severity: 'info', message: `channel=${channel}` });

          const mountEl = mountRef.current;
          if (!mounted || !mountEl) return;
          if (!window.Twitch || !window.Twitch.Player) throw new Error('Twitch embed missing');

          const parents = getEmbedParents();

          if (playerRef.current && typeof playerRef.current.destroy === 'function') {
            try { playerRef.current.destroy(); } catch {}
            playerRef.current = null;
          }

          await logEvent({ platform: 'twitch', event: 'LURKER2_TWITCH_PLAYER_CREATE', severity: 'info', message: `channel=${channel}`, details: { parents } });
          playerRef.current = new window.Twitch.Player(mountId, {
            channel,
            parent: parents,
            autoplay: true,
            muted: true,
            width: '100%',
            height: '100%',
          });

          let readyFired = false;
          const readyTimeout = setTimeout(() => {
            if (!mounted || readyFired) return;
            logEvent({ platform: 'twitch', event: 'LURKER2_TWITCH_READY_TIMEOUT', severity: 'warn', message: `channel=${channel}`, details: { parents } }).catch(() => {});
            throw new Error('Twitch READY timeout');
          }, 8000);

          playerRef.current.addEventListener(window.Twitch.Player.READY, () => {
            readyFired = true;
            clearTimeout(readyTimeout);
            logEvent({ platform: 'twitch', event: 'LURKER2_TWITCH_READY', severity: 'info', message: `channel=${channel}`, details: { parents } }).catch(() => {});
            try {
              if (typeof playerRef.current.play === 'function') playerRef.current.play();
            } catch {}
          });
        });
      } catch {
        // Fallback iframe (still official embed)
        const parents = getEmbedParents();
        const parentParam = encodeURIComponent(parents[0] || 'localhost');
        const url = `https://player.twitch.tv/?channel=${encodeURIComponent(channel)}&parent=${parentParam}&autoplay=true&muted=true`;
        if (mountRef.current) {
          logEvent({ platform: 'twitch', event: 'LURKER2_TWITCH_FALLBACK_IFRAME', severity: 'warn', message: `channel=${channel}`, details: { parent: parents[0] || 'localhost' } }).catch(() => {});
          mountRef.current.innerHTML = `<iframe src="${url}" allow="autoplay; fullscreen" frameborder="0" allowfullscreen style="position:absolute;inset:0;width:100%;height:100%;border:0"></iframe>`;
        }
      }
    };

    init();
    return () => {
      mounted = false;
      const mountEl = mountRef.current;
      if (playerRef.current && typeof playerRef.current.destroy === 'function') {
        try { playerRef.current.destroy(); } catch {}
      }
      playerRef.current = null;
      if (mountEl) mountEl.innerHTML = '';
    };
  }, [channel, mountId]);

  return (
    <div style={{ position: 'relative', width: '100%', borderRadius: 10, overflow: 'hidden', background: '#0b1220' }}>
      {/* Ensure >= 400x300 by layout; this wrapper maintains 16:9 */}
      <div style={{ paddingTop: '56.25%' }} />
      <div ref={mountRef} id={mountId} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
    </div>
  );
}

export default function Lurker2() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [assigned, setAssigned] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);

  const sessionId = useMemo(() => createSessionId(), []);

  useEffect(() => {
    const onUnload = () => {
      try {
        const base = ((import.meta?.env?.VITE_API_URL || '').trim()) || window.location.origin;
        const urlBase = `${String(base).replace(/\/$/, '')}/lurker2/session/close`;
        try {
          const token = localStorage.getItem('token');
          const qs = new URLSearchParams({ sessionId, page: 'lurker2' });
          fetch(`${urlBase}?${qs.toString()}`, {
            method: 'GET',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            keepalive: true,
            credentials: 'include',
          }).catch(() => {});
        } catch {}
      } catch {}
    };

    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [sessionId]);

  const [radar, setRadar] = useState(null);

  const expiresMs = useMemo(() => (expiresAt ? new Date(expiresAt).getTime() : 0), [expiresAt]);
  const [nowMs, setNowMs] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const remainingSec = useMemo(() => {
    if (!expiresMs) return null;
    return Math.max(0, Math.floor((expiresMs - nowMs) / 1000));
  }, [expiresMs, nowMs]);

  useEffect(() => {
    let mounted = true;
    let first = true;
    const tick = async () => {
      if (!mounted) return;
      if (first) {
        setLoading(true);
        setError('');
      }
      try {
        const a = await api.get('/lurker2/assignment', { params: { sessionId, page: 'lurker2' } });
        if (!mounted) return;
        setAssigned(a.data?.assigned || null);
        setExpiresAt(a.data?.expiresAt || null);
        if (first) {
          await logEvent({ platform: 'twitch', event: 'LURKER2_ASSIGNMENT_LOADED', severity: 'info', message: `login=${a.data?.assigned?.twitchLogin || ''}`, details: { windowKey: a.data?.windowKey, expiresAt: a.data?.expiresAt, activeSessions: a.data?.activeSessions } });
        }
      } catch (e) {
        if (!mounted) return;
        if (first) setError(e?.response?.data?.error || e?.message || 'Konnte Zuteilung nicht laden');
      } finally {
        if (!mounted) return;
        if (first) setLoading(false);
        first = false;
      }
    };

    tick().catch(() => {});
    const t = setInterval(() => tick().catch(() => {}), 25000);
    return () => {
      mounted = false;
      clearInterval(t);
    };
  }, [sessionId]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/lurker2/radar');
        setRadar(r.data);
      } catch {
        // radar is optional
      }
    })();
  }, []);

  useEffect(() => {
    if (remainingSec == null) return;
    if (remainingSec > 0) return;

    logEvent({ platform: 'twitch', event: 'LURKER2_ROTATE_RELOAD', severity: 'info', message: 'Rotation window ended; reloading' }).catch(() => {});
    // Spec: full reload to rebuild DOM + embed
    window.location.reload();
  }, [remainingSec]);

  useEffect(() => {
    const onVis = () => {
      const hidden = document.visibilityState !== 'visible';
      logEvent({ platform: 'twitch', event: hidden ? 'LURKER2_TAB_HIDDEN' : 'LURKER2_TAB_VISIBLE', severity: hidden ? 'warn' : 'info' }).catch(() => {});
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  const channel = assigned?.twitchLogin || '';
  const twitchUrl = channel ? `https://www.twitch.tv/${channel}` : '';
  const chatPopoutUrl = channel ? `https://www.twitch.tv/popout/${channel}/chat` : '';

  return (
    <div className="home-page">
      <section style={{ width: '100vw', margin: 0, padding: 0, position: 'relative', left: '-120px' }}>
        <h2 className="hero-title" style={{ textAlign: 'center', margin: '0 0 8px' }}>Lurker 2.0</h2>
        <p className="hero-desc" style={{ margin: '0 auto 24px', textAlign: 'center', maxWidth: 860 }}>
          Ein Stream pro Seite, sichtbar und offiziell eingebettet. Alle {20} Minuten wird automatisch neu zugeteilt, um Support fair zu verteilen.
        </p>
      </section>

      <section style={{ width: '100%', maxWidth: 1200, margin: '0 auto', padding: '0 16px 40px 16px', color: '#fff' }}>
        {error && <div style={{ color: 'tomato', marginBottom: 10 }}>{error}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: '1.7fr 1fr', gap: 16, alignItems: 'start' }}>
          <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: 12 }}>
            <h3 style={{ marginTop: 0, display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
              <span>Aktiver Stream</span>
              <span style={{ fontSize: 12, color: '#bbb' }}>{remainingSec != null ? `Wechsel in ${Math.floor(remainingSec / 60)}:${String(remainingSec % 60).padStart(2, '0')}` : ''}</span>
            </h3>

            {loading && <div>Lade…</div>}
            {!loading && !!channel && (
              <>
                <div style={{ minHeight: 300 }}>
                  <TwitchPlayer channel={channel} />
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                  <a className="btn" href={twitchUrl} target="_blank" rel="noreferrer" onClick={() => logEvent({ platform: 'twitch', event: 'LURKER2_OPEN_TWITCH', severity: 'info', message: twitchUrl, details: { channel } })}>Stream öffnen</a>
                  <a className="btn" href={chatPopoutUrl} target="_blank" rel="noreferrer" onClick={() => logEvent({ platform: 'twitch', event: 'LURKER2_OPEN_CHAT', severity: 'info', message: chatPopoutUrl, details: { channel } })}>Chat öffnen</a>
                  <button onClick={() => window.location.reload()} style={{ padding: '10px 14px' }}>Neu laden</button>
                </div>

                <div style={{ fontSize: 12, color: '#bbb', marginTop: 8 }}>
                  Hinweis: Autoplay startet standardmäßig stumm (Browser-Regeln). Du kannst im Player jederzeit Ton aktivieren.
                </div>
              </>
            )}

            {!loading && !channel && <div>Aktuell kein Stream verfügbar.</div>}
          </div>

          <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: 12 }}>
            <h3 style={{ marginTop: 0 }}>Live‑Radar (Top)</h3>
            {!radar && <div style={{ color: '#bbb' }}>Nicht geladen.</div>}
            {radar && (
              <div style={{ display: 'grid', gap: 8 }}>
                {(radar.live || []).slice(0, 8).map((x) => (
                  <div key={x.twitchLogin} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: 6 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.displayName}</div>
                      <div style={{ fontSize: 12, color: '#bbb', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.viewerCount} Viewer · {x.gameName || '—'}</div>
                    </div>
                    <a href={`/lurker/${encodeURIComponent(x.username)}`} className="btn" style={{ padding: '6px 10px', alignSelf: 'center' }} onClick={() => logEvent({ platform: 'twitch', event: 'LURKER2_OPEN_MEMBER', severity: 'info', details: { username: x.username, login: x.twitchLogin } })}>Profil</a>
                  </div>
                ))}
                {(radar.plannedNotLive || []).length > 0 && (
                  <div style={{ fontSize: 12, color: '#bbb', marginTop: 6 }}>
                    Geplant aber aktuell nicht live: {(radar.plannedNotLive || []).slice(0, 3).map(p => p.displayName).join(', ')}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
